import { useState, useMemo, useContext } from "react";
import { useImmer } from "use-immer";
import {
  useCreateThresholdMutation,
  useStockItemsListQuery,
  useUpdateBulkVisibilityMutation,
} from "./useStockItemsQueries";
import { AppContext } from "../../../store/AppScopeProvider";
import { AuthContext } from "../../../store";
import { useLocation } from "react-router-dom";
import { formatCost } from "../../../utils";

// Groups flat stocks array into sorted category → subcategory structure using IDs
const groupByCategory = (items = []) => {
  const categoryMap = {};

  items.forEach((item) => {
    const catKey = item.category_id;
    if (!categoryMap[catKey]) {
      categoryMap[catKey] = {
        id: item.category_id,
        name: item.category,
        display_order: item.category_display_order,
        subcategories: {},
      };
    }
    const subKey = item.subcategory_id;
    if (!categoryMap[catKey].subcategories[subKey]) {
      categoryMap[catKey].subcategories[subKey] = {
        id: item.subcategory_id,
        name: item.subcategory,
        display_order: item.subcategory_display_order,
        items: [],
      };
    }
    categoryMap[catKey].subcategories[subKey].items.push(item);
  });

  const categories = Object.values(categoryMap).sort(
    (a, b) => a.display_order - b.display_order,
  );

  categories.forEach((cat) => {
    cat.subcategories = Object.values(cat.subcategories).sort(
      (a, b) => a.display_order - b.display_order,
    );
  });

  return categories;
};

export const useStockItemsList = () => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("/store-admin");

  const { tableData, setTableState, setSearchState, clearAllFilters } =
    useContext(AppContext);
  const { auth } = useContext(AuthContext);
  const storeId = auth?.data?.selectedStoreId;
  const moduleName = "stockItems" + (isStoreAdmin ? "Store" : "Admin");

  const createThresholdMutation = useCreateThresholdMutation();
  const bulkVisibilityMutation = useUpdateBulkVisibilityMutation();

  const tableState = tableData[moduleName] ?? {
    data: { status: null, visibility: null },
    search: "",
  };

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  // Tracks local edits for threshold/visibility per item id
  const [itemEdits, updateItemEdits] = useImmer({});
  // Tracks inline validation errors per item id
  const [validationErrors, updateValidationErrors] = useImmer({});
  const [selectionState, updateSelectionState] = useImmer({
    selectedItems: [],
    resetSelection: null,
  });

  const filters = tableState.data;
  const search = tableState.search;

  const { data: rawItems } = useStockItemsListQuery();

  const filteredGroupedCategories = useMemo(() => {
    if (!rawItems) return [];

    const searchValue = search.toLowerCase();

    const filtered = rawItems.filter((item) => {
      if (search && !item.name.toLowerCase().includes(searchValue)) {
        return false;
      }

      if (filters.status) {
        const isActive = item.status === 1;

        if (filters.status.value === 1 && !isActive) return false;
        if (filters.status.value === 0 && isActive) return false;
      }

      if (filters.visibility) {
        const isVisible = item.visibility === 1;

        if (filters.visibility.value === 1 && !isVisible) return false;
        if (filters.visibility.value === 0 && isVisible) return false;
      }

      return true;
    });

    return groupByCategory(filtered);
  }, [rawItems, search, filters.status, filters.visibility]);

  const selectedCategoryData = useMemo(
    () =>
      filteredGroupedCategories.find(
        (category) => category.id === selectedCategoryId,
      ) ?? null,
    [filteredGroupedCategories, selectedCategoryId],
  );

  // Build table rows for selected category with merged local edits
  const tableRows = useMemo(() => {
    if (!selectedCategoryData) return [];

    return selectedCategoryData.subcategories.map((sub) => ({
      id: `subcategory-${sub.id}`,
      itemName: sub.name,
      subRows: sub.items.map((item) => ({
        id: item.id,
        itemName: item.name,
        status: item.status,
        stockUnit: item.unit,
        stock_code: item.stock_code,
        cost: `$${formatCost(item.total_cost_exc_gst || 0)}`,
        type: item.type,
        type_id: item.type_id,
        threshold_unit:
          itemEdits[item.id]?.threshold_unit ??
          item.threshold?.threshold_qty ??
          "",
        threshold_percentage:
          itemEdits[item.id]?.threshold_percentage ??
          item.threshold?.threshold_percentage ??
          "",
        visibility: itemEdits[item.id]?.visibility ?? item.visibility ?? 1,
        threshold_id: item.threshold?.id ?? null,
      })),
    }));
  }, [selectedCategoryData, itemEdits]);

  const handleCategorySelect = (category) => {
    setSelectedCategoryId(category.id);
    setEditingRowId(null);
  };

  const handleSearchChange = (value) => {
    setSearchState({ tableName: moduleName, key: "search", value });
  };

  const handleStatusChange = (event, newValue) => {
    setTableState({ tableName: moduleName, key: "status", value: newValue });
  };

  const handleVisibilityChange = (event, newValue) => {
    setTableState({
      tableName: moduleName,
      key: "visibility",
      value: newValue,
    });
  };

  const handleClearFilters = () => {
    clearAllFilters({ tableName: moduleName });
  };

  const validateThresholdPair = (id, qty, pct) => {
    const qtyVal = Number(qty);
    const pctVal = Number(pct);
    const qtyFilled = qty !== "" && qtyVal > 0;
    const pctFilled = pct !== "" && pctVal > 0;
    const qtyHasValue = qty !== "";
    const pctHasValue = pct !== "";

    let qtyError = null;
    let pctError = null;

    if (qtyHasValue && qtyVal <= 0) {
      qtyError = "Must be greater than 0";
    }
    if (pctHasValue && pctVal <= 0) {
      pctError = "Must be greater than 0";
    }
    if (pctFilled && !qtyFilled) {
      qtyError = qtyError || "Required";
    }
    if (qtyFilled && !pctFilled) {
      pctError = pctError || "Required";
    }
    if (pctHasValue && pctVal > 100) {
      pctError = "Must be between 1 to 100";
    }

    updateValidationErrors((draft) => {
      if (!draft[id]) draft[id] = {};
      draft[id].threshold_unit = qtyError;
      draft[id].threshold_percentage = pctError;
    });

    return !qtyError && !pctError;
  };

  const handleEdit = (item) => {
    // If currently editing and clicking save, submit the threshold
    if (editingRowId === item.id) {
      const edits = itemEdits[item.id];
      if (edits) {
        const qty = edits.threshold_unit ?? item.threshold_unit ?? "";
        const pct =
          edits.threshold_percentage ?? item.threshold_percentage ?? "";
        if (!validateThresholdPair(item.id, qty, pct)) {
          return;
        }
        const formData = new FormData();
        formData.append("store_id", storeId);
        formData.append("stockable_item_id", item.id);
        formData.append("threshold_qty", qty || "");
        formData.append("threshold_percentage", pct || "");
        formData.append("visibility", edits.visibility ?? item.visibility ?? 1);
        createThresholdMutation.mutate(formData, {
          onSuccess: () => {
            setEditingRowId(null);
            updateItemEdits((draft) => {
              delete draft[item.id];
            });
            updateValidationErrors((draft) => {
              delete draft[item.id];
            });
          },
        });
        return;
      }
      setEditingRowId(null);
      return;
    }
    setEditingRowId(item.id);
    updateItemEdits((draft) => {
      draft[item.id] = {
        threshold_unit: item.threshold_unit ?? "",
        threshold_percentage: item.threshold_percentage ?? "",
        visibility: item.visibility ?? 1,
      };
    });
  };

  const handleCancelEdit = (id) => {
    setEditingRowId(null);
    updateItemEdits((draft) => {
      delete draft[id];
    });
    updateValidationErrors((draft) => {
      delete draft[id];
    });
  };

  const handleThresholdChange = (id, field, value) => {
    updateItemEdits((draft) => {
      if (!draft[id]) draft[id] = {};
      draft[id][field] = value;
    });
    const edits = { ...itemEdits[id], [field]: value };
    const qty = edits.threshold_unit ?? "";
    const pct = edits.threshold_percentage ?? "";
    validateThresholdPair(id, qty, pct);
  };

  const handleVisibilityToggle = (item) => {
    const newVisibility = item.visibility === 1 ? 0 : 1;
    const formData = new FormData();
    formData.append("store_id", storeId);
    formData.append("stockable_item_id", item.id);
    formData.append("threshold_qty", item.threshold_unit || "");
    formData.append("threshold_percentage", item.threshold_percentage || "");
    formData.append("visibility", newVisibility);
    createThresholdMutation.mutate(formData);
  };

  const updateBulkUpdateRowSelection = (selectedItems) => {
    updateSelectionState((draft) => {
      draft.selectedItems = selectedItems;
      if (draft.resetSelection) draft.resetSelection = null;
    });
  };
  const onBulkUpdateConfirm = ({ cancelBulkUpdateToggle, type }) => {
    const stockItemIds = selectionState.selectedItems
      .map((item) => item.id)
      .filter(Number.isInteger);
    bulkVisibilityMutation.mutate(
      {
        storeId,
        data: {
          stock_item_ids: stockItemIds,
          status: type ? "enable" : "disable",
        },
      },
      {
        onSuccess: (responseData) => {
          updateSelectionState((draft) => {
            draft.selectedItems = [];
            draft.resetSelection = true;
          });
          cancelBulkUpdateToggle();
        },
      },
    );
  };

  const disableClearButton = !filters.status && !filters.visibility;
  const isSaving = createThresholdMutation.isPending;

  return {
    selectedCategoryId,
    groupedCategories: filteredGroupedCategories,
    tableData: tableRows,
    itemEdits,
    validationErrors,
    filters,
    editingRowId,
    isSaving,
    search,
    disableClearButton,
    selectionState,
    isBulkVisibilityUpdating: bulkVisibilityMutation.isPending,
    handleCategorySelect,
    handleSearchChange,
    handleStatusChange,
    handleVisibilityChange,
    handleClearFilters,
    handleEdit,
    handleCancelEdit,
    handleThresholdChange,
    handleVisibilityToggle,
    onBulkUpdateConfirm,
    updateBulkUpdateRowSelection,
  };
};
